import { useState, useEffect } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useTheme } from "../../context/ThemeContext";

// ─── Shared primitives ───────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div style={{ marginBottom: "28px" }}>
    <div style={{
      fontSize: "10px", letterSpacing: "0.12em", color: "var(--text-muted)",
      fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase",
      marginBottom: "14px", paddingBottom: "8px", borderBottom: "1px solid var(--border)",
    }}>{title}</div>
    {children}
  </div>
);

const InfoRow = ({ label, value, badge }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "13px 0", borderBottom: "1px solid var(--border)",
  }}>
    <span style={{ fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{label}</span>
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {badge && (
        <span style={{
          padding: "2px 8px", borderRadius: "10px", fontSize: "10px",
          background: badge === "Active" ? "#1a2d1a" : "#2d1a1a",
          color: badge === "Active" ? "#6fcf6f" : "#e07070",
          fontFamily: "'JetBrains Mono', monospace",
        }}>{badge}</span>
      )}
      <span style={{ fontSize: "13px", color: "#D4AF37" }}>{value}</span>
    </div>
  </div>
);

const ActionButton = ({ label, variant = "default", onClick, disabled }) => {
  const [hover, setHover] = useState(false);
  const styles = {
    default: { bg: hover ? "var(--bg-hover)" : "var(--bg-card)", border: hover ? "var(--border-active)" : "var(--border-strong)", color: "#D4AF37" },
    danger:  { bg: hover ? "#2d1a1a" : "var(--bg-card)", border: hover ? "#5a2a2a" : "var(--border-strong)", color: "#e07070" },
    primary: { bg: hover ? "#D4AF37" : "#b0a280", border: hover ? "#D4AF37" : "#b0a280", color: "#0D1410" },
  };
  const s = styles[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: "9px 18px", borderRadius: "8px",
        background: s.bg, border: `1px solid ${s.border}`,
        fontSize: "12px", color: s.color,
        fontFamily: "'JetBrains Mono', monospace", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1, transition: "all 0.2s",
      }}
    >{label}</button>
  );
};

const Toggle = ({ on, onToggle }) => (
  <div onClick={onToggle} style={{
    width: "40px", height: "22px", borderRadius: "11px",
    background: on ? "#D4AF37" : "#1E2C1E",
    position: "relative", cursor: "pointer", transition: "background 0.25s",
  }}>
    <div style={{
      position: "absolute", top: "3px", left: on ? "21px" : "3px",
      width: "16px", height: "16px", borderRadius: "50%",
      background: on ? "#0D1410" : "#555", transition: "left 0.25s",
    }} />
  </div>
);

// ─── Modal wrapper ────────────────────────────────────────────────────────────

const Modal = ({ title, onClose, children }) => (
  <div style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: "24px",
  }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "18px",
      padding: "28px", width: "100%", maxWidth: "460px",
      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "24px",
      }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "var(--text-primary)", fontFamily: "'Capsule Sans', sans-serif" }}>
          {title}
        </h3>
        <button onClick={onClose} style={{
          background: "none", border: "none", color: "var(--text-muted)", fontSize: "20px",
          cursor: "pointer", padding: "0 4px", lineHeight: 1,
        }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom: "16px" }}>
    <label style={{
      display: "block", fontSize: "11px", color: "var(--text-muted)",
      letterSpacing: "0.1em", textTransform: "uppercase",
      fontFamily: "'JetBrains Mono', monospace", marginBottom: "8px",
    }}>{label}</label>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: "100%", padding: "10px 14px",
        background: "var(--bg-main)", border: "1px solid var(--border-strong)",
        borderRadius: "8px", color: "var(--text-primary)", fontSize: "13px",
        fontFamily: "inherit", outline: "none", cursor: "pointer",
      }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

// ─── Upgrade Modal ────────────────────────────────────────────────────────────

const FEATURES = [
  { label: "Unlimited bank connections via Plaid",    trial: false, premium: true  },
  { label: "AI-powered budget insights & projections", trial: true,  premium: true  },
  { label: "Investment portfolio tracking",            trial: true,  premium: true  },
  { label: "Recurring transaction analysis",           trial: true,  premium: true  },
  { label: "CSV & HTML report exports",                trial: false, premium: true  },
  { label: "Priority support",                         trial: false, premium: true  },
  { label: "Historical data beyond 90 days",           trial: false, premium: true  },
];

function UpgradeModal({ daysLeft, onConfirm, onClose }) {
  const [hover, setHover] = useState(false);
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "24px",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-strong)",
        borderRadius: "20px", width: "100%", maxWidth: "520px",
        boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
        overflow: "hidden",
        animation: "modalIn 0.22s cubic-bezier(0.4,0,0.2,1)",
      }}>
        <style>{`
          @keyframes modalIn {
            from { opacity: 0; transform: scale(0.96) translateY(8px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>

        {/* Gold gradient header */}
        <div style={{
          padding: "28px 28px 24px",
          background: "linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04))",
          borderBottom: "1px solid rgba(212,175,55,0.15)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: "22px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "var(--text-primary)", marginBottom: "4px" }}>
                Upgrade to Premium
              </div>
              {daysLeft > 0 && (
                <div style={{ fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace" }}>
                  {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining in your free trial
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: "20px", cursor: "pointer", padding: "0 4px" }}>×</button>
          </div>
        </div>

        {/* Plan comparison */}
        <div style={{ padding: "20px 28px" }}>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr auto auto",
            gap: "0 24px", marginBottom: "4px",
          }}>
            <div />
            <div style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase", letterSpacing: "0.1em", textAlign: "center", paddingBottom: "8px" }}>Free Trial</div>
            <div style={{ fontSize: "10px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase", letterSpacing: "0.1em", textAlign: "center", paddingBottom: "8px" }}>Premium</div>
          </div>
          {FEATURES.map(f => (
            <div key={f.label} style={{
              display: "grid", gridTemplateColumns: "1fr auto auto",
              gap: "0 24px", padding: "9px 0", borderBottom: "1px solid var(--border)",
              alignItems: "center",
            }}>
              <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{f.label}</span>
              <span style={{ textAlign: "center", fontSize: "13px", color: f.trial ? "#6fcf6f" : "var(--text-secondary)" }}>
                {f.trial ? "✓" : "—"}
              </span>
              <span style={{ textAlign: "center", fontSize: "13px", color: "#6fcf6f" }}>✓</span>
            </div>
          ))}
        </div>

        {/* Price + CTA */}
        <div style={{ padding: "16px 28px 28px" }}>
          <div style={{
            background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)",
            borderRadius: "12px", padding: "16px 20px", marginBottom: "16px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: "22px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "#D4AF37" }}>$9.99<span style={{ fontSize: "14px", color: "var(--text-muted)" }}>/mo</span></div>
              <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", marginTop: "2px" }}>Billed monthly · Cancel anytime</div>
            </div>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", textAlign: "right" }}>
              $99.99/yr<br />
              <span style={{ color: "#52D68A" }}>Save ~17%</span>
            </div>
          </div>
          <button
            onClick={onConfirm}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
              width: "100%", padding: "13px",
              background: hover ? "#d4c4a6" : "#D4AF37",
              border: "none", borderRadius: "10px",
              fontSize: "13px", fontWeight: "600", color: "#0D1410",
              fontFamily: "'JetBrains Mono', monospace", cursor: "pointer",
              transition: "background 0.2s",
            }}
          >Start Premium — $9.99/month</button>
          <div style={{ textAlign: "center", marginTop: "10px", fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
            No contracts · Cancel anytime from Account Settings
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const PREFS_KEY = "ledger_preferences";

const loadPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; }
};

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

export default function AccountInfoPage({ planState, onUpgrade, token, totpEnabled: initTotp, onTotpChange }) {
  const isMobileLayout = useIsMobile();
  const { isDark, applyTheme } = useTheme();
  const saved     = loadPrefs();
  const isTrial   = planState && planState.plan === "trial";
  const daysLeft  = isTrial ? planState.daysLeft  : 0;
  const trialEnd  = isTrial ? planState.trialEnd   : "";

  // ── 2FA state ─────────────────────────────────────────────────────────────
  const [twoFAEnabled,    setTwoFAEnabled]    = useState(initTotp || false);
  const [showSetup,       setShowSetup]       = useState(false);  // setup modal
  const [showDisable,     setShowDisable]     = useState(false);  // disable modal
  const [totpStep,        setTotpStep]        = useState(1);       // 1=qr, 2=code, 3=backup
  const [qrDataUrl,       setQrDataUrl]       = useState("");
  const [totpSecret,      setTotpSecret]      = useState("");
  const [totpCode,        setTotpCode]        = useState("");
  const [backupCodes,     setBackupCodes]     = useState([]);
  const [disableCode,     setDisableCode]     = useState("");
  const [totpLoading,     setTotpLoading]     = useState(false);
  const [totpError,       setTotpError]       = useState("");
  // Keep in sync if parent refreshes
  useEffect(() => { setTwoFAEnabled(initTotp || false); }, [initTotp]);

  // Load notification preferences from backend on mount
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/v1/notifications/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setEmailAlerts(d.email_alerts ?? true);
        setPushNotif(d.push_notif ?? false);
        setWeeklyReport(d.weekly_summary ?? false);
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const [emailAlerts,    setEmailAlerts]    = useState(true);
  const [pushNotif,      setPushNotif]      = useState(false);
  const [weeklyReport,   setWeeklyReport]   = useState(false);
  const [notifLoading,   setNotifLoading]   = useState(false);
  const [notifError,     setNotifError]     = useState("");
  const [editingName,  setEditingName]  = useState(false);
  const [name,         setName]         = useState("Revanth Rondla");
  const [nameInput,    setNameInput]    = useState("Revanth Rondla");
  const [showPwChange, setShowPwChange] = useState(false);
  const [pwForm,       setPwForm]       = useState({ current: "", next: "", confirm: "" });
  const [pwSaved,      setPwSaved]      = useState(false);
  const [pwLoading,    setPwLoading]    = useState(false);
  const [pwError,      setPwError]      = useState("");

  // Modals
  const [modal,       setModal]       = useState(null);  // "billing" | "cancel" | "preferences"
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Preferences state
  const [prefs, setPrefs] = useState({
    currency:   saved.currency   || "USD",
    timezone:   saved.timezone   || "America/Chicago",
    dateFormat: saved.dateFormat || "MM/DD/YYYY",
  });
  const [prefsDraft,  setPrefsDraft]  = useState(prefs);
  const [prefsSaved,  setPrefsSaved]  = useState(false);

  // Cancel plan flow
  const [cancelStep,   setCancelStep]  = useState(1); // 1 = confirm, 2 = done
  const [cancelReason, setCancelReason] = useState("");

  const handleSaveName = () => { setName(nameInput); setEditingName(false); };

  // ── Save notification preferences to backend ──────────────────────────────
  const saveNotifPrefs = async (next) => {
    if (!token) return;
    setNotifError("");
    setNotifLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/notifications/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(next),
      });
      if (!r.ok) {
        let msg = "Failed to save notification preferences.";
        try {
          const errBody = await r.json();
          if (errBody?.details?.length) {
            msg = errBody.details.map(d => d.message).join(", ");
          } else if (errBody?.error) {
            msg = errBody.error;
          }
        } catch {}
        setNotifError(msg);
      }
    } catch { setNotifError("Connection error. Please check your internet and try again."); }
    finally { setNotifLoading(false); }
  };

  // ── Toggle handlers ────────────────────────────────────────────────────────
  const handleEmailAlertsToggle = () => {
    const next = !emailAlerts;
    setEmailAlerts(next);
    saveNotifPrefs({ emailAlerts: next, pushNotif, weeklyReport });
  };

  const handleWeeklyReportToggle = () => {
    const next = !weeklyReport;
    setWeeklyReport(next);
    saveNotifPrefs({ emailAlerts, pushNotif, weeklyReport: next });
  };

  const handlePushNotifToggle = async () => {
    const next = !pushNotif;
    setNotifError("");

    if (next) {
      // Enabling: request browser permission + register service worker
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        setNotifError("Push notifications are not supported in this browser.");
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setNotifError("Notification permission was denied. Please enable it in your browser settings.");
        return;
      }
      try {
        // Fetch VAPID public key from backend
        const keyRes = await fetch(`${API}/api/v1/notifications/vapid-key`);
        const { publicKey } = await keyRes.json();
        if (!publicKey) { setNotifError("Push notifications are not configured yet."); return; }

        const reg = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        // Convert VAPID key from base64url to Uint8Array
        const padding  = '='.repeat((4 - publicKey.length % 4) % 4);
        const b64      = (publicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawKey   = Uint8Array.from(atob(b64), c => c.charCodeAt(0));

        const subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: rawKey,
        });

        // Send subscription object to backend
        await fetch(`${API}/api/v1/notifications/push/subscribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ subscription: subscription.toJSON() }),
        });

        setPushNotif(true);
        saveNotifPrefs({ emailAlerts, pushNotif: true, weeklyReport });
      } catch (err) {
        setNotifError("Failed to set up push notifications: " + (err.message || "Unknown error"));
      }
    } else {
      // Disabling: unsubscribe from push manager + notify backend
      try {
        if ('serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.getRegistration('/sw.js');
          if (reg) {
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
              await fetch(`${API}/api/v1/notifications/push/unsubscribe`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ endpoint: sub.endpoint }),
              });
              await sub.unsubscribe();
            }
          }
        }
        setPushNotif(false);
        saveNotifPrefs({ emailAlerts, pushNotif: false, weeklyReport });
      } catch (err) {
        setNotifError("Failed to disable push notifications.");
      }
    }
  };

  const handleSavePw = async () => {
    setPwError("");
    if (!pwForm.current) { setPwError("Please enter your current password."); return; }
    if (pwForm.next !== pwForm.confirm) { setPwError("New passwords do not match."); return; }
    if (pwForm.next.length < 8) { setPwError("New password must be at least 8 characters."); return; }
    if (!/[A-Z]/.test(pwForm.next)) { setPwError("New password must contain at least one uppercase letter."); return; }
    if (!/[0-9]/.test(pwForm.next)) { setPwError("New password must contain at least one number."); return; }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwForm.next)) { setPwError("New password must contain at least one special character (e.g. !@#$)."); return; }
    setPwLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.next }),
      });
      const d = await r.json();
      if (r.ok) {
        setPwSaved(true);
        setTimeout(() => { setPwSaved(false); setShowPwChange(false); setPwForm({ current: "", next: "", confirm: "" }); setPwError(""); }, 1800);
      } else {
        setPwError(d.errors?.[0]?.msg || d.message || d.error || "Failed to update password.");
      }
    } catch { setPwError("Connection error. Please try again."); }
    finally { setPwLoading(false); }
  };

  const handleSavePrefs = () => {
    setPrefs(prefsDraft);
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefsDraft));
    setPrefsSaved(true);
    setTimeout(() => { setPrefsSaved(false); setModal(null); }, 1200);
  };

  const openPrefs = () => { setPrefsDraft(prefs); setPrefsSaved(false); setModal("preferences"); };
  const openBilling = () => setModal("billing");
  const openCancel  = () => { setCancelStep(1); setCancelReason(""); setModal("cancel"); };

  const handleConfirmCancel = () => setCancelStep(2);

  return (
    <div style={{ maxWidth: isMobileLayout ? "100%" : "780px" }}>

      {/* ── Profile header ───────────────────────────────────────── */}
      <div style={{
        background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "16px",
        padding: isMobileLayout ? "20px" : "28px", marginBottom: "28px",
        display: "flex", alignItems: isMobileLayout ? "flex-start" : "center",
        flexDirection: isMobileLayout ? "column" : "row", gap: "24px",
      }}>
        <div style={{
          width: "72px", height: "72px", borderRadius: "50%", flexShrink: 0,
          background: "linear-gradient(135deg, #D4AF37 0%, #9A7C1E 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "28px", fontWeight: "700", color: "#0D1410",
          fontFamily: "'Capsule Sans', sans-serif",
          boxShadow: "0 4px 20px rgba(212,175,55,0.2)",
        }}>R</div>

        <div style={{ flex: 1 }}>
          {editingName ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
              <input
                autoFocus value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleSaveName(); if (e.key === "Escape") setEditingName(false); }}
                style={{
                  background: "var(--bg-card)", border: "1px solid #D4AF37", borderRadius: "6px",
                  padding: "6px 10px", fontSize: "18px", color: "var(--text-primary)",
                  fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", outline: "none",
                }}
              />
              <ActionButton label="Save" variant="primary" onClick={handleSaveName} />
              <ActionButton label="Cancel" onClick={() => setEditingName(false)} />
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
              <h2 style={{ fontSize: "20px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)", margin: 0 }}>
                {name}
              </h2>
              <span
                onClick={() => { setEditingName(true); setNameInput(name); }}
                style={{ fontSize: "12px", color: "var(--text-muted)", cursor: "pointer", padding: "2px 6px", borderRadius: "4px" }}
                onMouseEnter={e => e.currentTarget.style.color = "#D4AF37"}
                onMouseLeave={e => e.currentTarget.style.color = "#555"}
              >✎ edit</span>
            </div>
          )}
          <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>revanth@ledger.app</div>
          <div style={{ marginTop: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {isTrial ? (
              <>
                <span style={{ padding: "3px 10px", borderRadius: "10px", fontSize: "10px", background: "#1f1a09", border: "1px solid #3a2f1a", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace" }}>
                  ✦ FREE TRIAL
                </span>
                <span style={{
                  padding: "3px 10px", borderRadius: "10px", fontSize: "10px",
                  background: daysLeft <= 3 ? "#2d1a1a" : "#1a2d1a",
                  border: `1px solid ${daysLeft <= 3 ? "#5a2a2a" : "#1e3a1e"}`,
                  color: daysLeft <= 3 ? "#e07070" : "#52D68A",
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {daysLeft <= 3 ? `⏰ ${daysLeft}d left` : `● ${daysLeft} days remaining`}
                </span>
              </>
            ) : (
              <>
                <span style={{ padding: "3px 10px", borderRadius: "10px", fontSize: "10px", background: "#2a1f0a", border: "1px solid #3a2f1a", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace" }}>✦ PREMIUM PLAN</span>
                <span style={{ padding: "3px 10px", borderRadius: "10px", fontSize: "10px", background: "#1a2a1a", border: "1px solid #1e2e1e", color: "#6fcf6f", fontFamily: "'JetBrains Mono', monospace" }}>● Active</span>
              </>
            )}
          </div>
        </div>

        <div style={{ textAlign: isMobileLayout ? "left" : "right" }}>
          <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "4px" }}>MEMBER SINCE</div>
          <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Jan 2024</div>
        </div>
      </div>

      {/* ── Two-column layout ─────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobileLayout ? "1fr" : "1fr 1fr", gap: "20px" }}>

        {/* Left column */}
        <div>
          {/* Account Details */}
          <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px", marginBottom: "20px" }}>
            <Section title="Account Details">
              <InfoRow label="EMAIL"   value="revanth@ledger.app" />
              <InfoRow label="USER ID" value="#00053" />
              {isTrial ? (
                <>
                  <InfoRow label="PLAN"       value="Free Trial" badge="Active" />
                  <InfoRow label="TRIAL END"  value={trialEnd} />
                  <InfoRow label="DAYS LEFT"  value={`${daysLeft} of ${planState.totalDays} days`} />
                </>
              ) : (
                <>
                  <InfoRow label="PLAN"        value="Premium — $9.99/mo" badge="Active" />
                  <InfoRow label="BILLING"     value="Mar 1, 2026" />
                  <InfoRow label="NEXT CHARGE" value="Apr 1, 2026" />
                </>
              )}
            </Section>
            {isTrial ? (
              <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
                <ActionButton label="Upgrade to Premium" variant="primary" onClick={() => setShowUpgrade(true)} />
              </div>
            ) : (
              <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
                <ActionButton label="Manage Billing" onClick={openBilling} />
                <ActionButton label="Cancel Plan" variant="danger" onClick={openCancel} />
              </div>
            )}
          </div>

          {/* Trial upgrade CTA */}
          {isTrial && (
            <div style={{
              background: "linear-gradient(135deg, rgba(212,175,55,0.1), rgba(212,175,55,0.03))",
              border: "1px solid rgba(212,175,55,0.22)", borderRadius: "16px",
              padding: "20px 22px", marginBottom: "20px",
            }}>
              <div style={{ fontSize: "15px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)", marginBottom: "6px" }}>
                Unlock everything with Premium
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.7, marginBottom: "14px" }}>
                Plaid bank connections · Unlimited history · Report exports · Priority support
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "14px" }}>
                <span style={{ fontSize: "26px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "#D4AF37" }}>$9.99</span>
                <span style={{ fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>/month · cancel anytime</span>
              </div>
              <ActionButton label="Upgrade Now →" variant="primary" onClick={() => setShowUpgrade(true)} />
            </div>
          )}

          {/* Preferences */}
          <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px" }}>
            <Section title="Preferences">
              <InfoRow label="CURRENCY"    value={prefs.currency} />
              <InfoRow label="TIMEZONE"    value={prefs.timezone} />
              <InfoRow label="DATE FORMAT" value={prefs.dateFormat} />
            </Section>
            <ActionButton label="Edit Preferences" onClick={openPrefs} />
          </div>
        </div>

        {/* Right column */}
        <div>
          {/* Security */}
          <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px", marginBottom: "20px" }}>
            <Section title="Security">
              {/* ── 2FA toggle row ───────────────────────────────────────────────── */}
              <div style={{ padding: "13px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: "13px", color: "#D4AF37", display: "flex", alignItems: "center", gap: "8px" }}>
                      Two-Factor Auth
                      {twoFAEnabled && <span style={{ fontSize: "10px", padding: "2px 7px", borderRadius: "8px", background: "#1a2d1a", color: "#6fcf6f", fontFamily: "'JetBrains Mono', monospace" }}>ACTIVE</span>}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "3px" }}>
                      {twoFAEnabled ? "Authenticator app (TOTP)" : "Adds a second login step"}
                    </div>
                  </div>
                  <Toggle
                    on={twoFAEnabled}
                    onToggle={() => {
                      if (twoFAEnabled) { setDisableCode(""); setTotpError(""); setShowDisable(true); }
                      else { setTotpStep(1); setTotpCode(""); setTotpError(""); setQrDataUrl(""); setShowSetup(true); }
                    }}
                  />
                </div>
              </div>

              {/* ── 2FA setup modal ──────────────────────────────────────────────────── */}
              {showSetup && (
                <Modal title={totpStep === 3 ? "Save your backup codes" : "Set up Two-Factor Auth"} onClose={() => setShowSetup(false)}>
                  {totpError && <div style={{ background: "#2d1a1a", border: "1px solid #5a2a2a", borderRadius: "8px", padding: "10px 14px", color: "#e07070", fontSize: "12px", marginBottom: "16px" }}>{totpError}</div>}

                  {/* Step 1: Show QR code */}
                  {totpStep === 1 && (
                    <>
                      <p style={{ color: "var(--text-muted)", fontSize: "13px", lineHeight: "1.6", marginBottom: "16px" }}>
                        Open your authenticator app (Google Authenticator, Authy, etc.) and scan the QR code below.
                      </p>
                      {!qrDataUrl ? (
                        <div style={{ textAlign: "center", padding: "24px", color: "var(--text-faint)" }}>
                          <button
                            onClick={async () => {
                              setTotpLoading(true); setTotpError("");
                              try {
                                const r = await fetch(`${API}/api/v1/auth/totp/setup`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
                                const d = await r.json();
                                if (r.ok) { setQrDataUrl(d.qrDataUrl); setTotpSecret(d.secret); }
                                else setTotpError(d.error || "Failed to start setup.");
                              } catch { setTotpError("Network error."); }
                              finally { setTotpLoading(false); }
                            }}
                            style={{ padding: "10px 20px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", border: "none", borderRadius: "8px", color: "#111", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}
                          >{totpLoading ? "Loading…" : "Generate QR Code"}</button>
                        </div>
                      ) : (
                        <>
                          <div style={{ textAlign: "center", margin: "0 auto 16px", background: "#fff", padding: "12px", borderRadius: "10px", display: "inline-block", width: "fit-content" }}>
                            <img src={qrDataUrl} alt="2FA QR Code" style={{ display: "block", width: "180px", height: "180px" }} />
                          </div>
                          <div style={{ marginBottom: "16px" }}>
                            <div style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "6px" }}>MANUAL ENTRY KEY</div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", color: "#D4AF37", background: "var(--bg-main)", padding: "10px 12px", borderRadius: "8px", letterSpacing: "0.15em", wordBreak: "break-all" }}>{totpSecret}</div>
                          </div>
                          <ActionButton label="I've scanned the code →" variant="primary" onClick={() => { setTotpStep(2); setTotpCode(""); setTotpError(""); }} />
                        </>
                      )}
                    </>
                  )}

                  {/* Step 2: Confirm with first code */}
                  {totpStep === 2 && (
                    <>
                      <p style={{ color: "var(--text-muted)", fontSize: "13px", lineHeight: "1.6", marginBottom: "16px" }}>
                        Enter the 6-digit code shown in your authenticator app to confirm setup.
                      </p>
                      <input
                        type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                        placeholder="000 000" value={totpCode} autoFocus
                        onChange={e => { setTotpCode(e.target.value); setTotpError(""); }}
                        style={{ width: "100%", padding: "12px", textAlign: "center", letterSpacing: "0.3em", fontSize: "20px", fontFamily: "'JetBrains Mono', monospace", background: "var(--bg-main)", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-primary)", outline: "none", marginBottom: "16px", boxSizing: "border-box" }}
                      />
                      <div style={{ display: "flex", gap: "10px" }}>
                        <ActionButton label="← Back" onClick={() => setTotpStep(1)} />
                        <ActionButton label={totpLoading ? "Enabling…" : "Enable 2FA"} variant="primary" disabled={totpLoading || totpCode.length < 6}
                          onClick={async () => {
                            setTotpLoading(true); setTotpError("");
                            try {
                              const r = await fetch(`${API}/api/v1/auth/totp/enable`, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ code: totpCode }) });
                              const d = await r.json();
                              if (r.ok) { setBackupCodes(d.backupCodes); setTotpStep(3); }
                              else setTotpError(d.error || "Invalid code — please try again.");
                            } catch { setTotpError("Network error."); }
                            finally { setTotpLoading(false); }
                          }}
                        />
                      </div>
                    </>
                  )}

                  {/* Step 3: Show backup codes */}
                  {totpStep === 3 && (
                    <>
                      <p style={{ color: "#e8a060", fontSize: "13px", lineHeight: "1.6", marginBottom: "14px", background: "#1e150a", border: "1px solid #5a3a10", borderRadius: "8px", padding: "10px 14px" }}>
                        ⚠️ Save these backup codes somewhere safe. Each can be used once if you lose your authenticator app. <strong>They won't be shown again.</strong>
                      </p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginBottom: "20px" }}>
                        {backupCodes.map((code, i) => (
                          <div key={i} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", color: "#D4AF37", background: "var(--bg-main)", padding: "8px 10px", borderRadius: "6px", textAlign: "center", letterSpacing: "0.12em" }}>{code}</div>
                        ))}
                      </div>
                      <ActionButton label="I've saved my codes — Done ✓" variant="primary"
                        onClick={() => {
                          setTwoFAEnabled(true);
                          if (onTotpChange) onTotpChange(true);
                          setShowSetup(false);
                          setTotpStep(1); setQrDataUrl(""); setTotpSecret(""); setTotpCode(""); setBackupCodes([]);
                        }}
                      />
                    </>
                  )}
                </Modal>
              )}

              {/* ── 2FA disable modal ────────────────────────────────────────────────── */}
              {showDisable && (
                <Modal title="Disable Two-Factor Auth" onClose={() => setShowDisable(false)}>
                  {totpError && <div style={{ background: "#2d1a1a", border: "1px solid #5a2a2a", borderRadius: "8px", padding: "10px 14px", color: "#e07070", fontSize: "12px", marginBottom: "16px" }}>{totpError}</div>}
                  <p style={{ color: "var(--text-muted)", fontSize: "13px", lineHeight: "1.6", marginBottom: "16px" }}>
                    Enter your 6-digit authenticator code (or a backup code) to disable 2FA.
                  </p>
                  <input
                    type="text" inputMode="numeric" maxLength={8} placeholder="000 000" value={disableCode} autoFocus
                    onChange={e => { setDisableCode(e.target.value); setTotpError(""); }}
                    style={{ width: "100%", padding: "12px", textAlign: "center", letterSpacing: "0.3em", fontSize: "20px", fontFamily: "'JetBrains Mono', monospace", background: "var(--bg-main)", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-primary)", outline: "none", marginBottom: "16px", boxSizing: "border-box" }}
                  />
                  <ActionButton label={totpLoading ? "Disabling…" : "Disable 2FA"} variant="danger" disabled={totpLoading || !disableCode}
                    onClick={async () => {
                      setTotpLoading(true); setTotpError("");
                      try {
                        const r = await fetch(`${API}/api/v1/auth/totp/disable`, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ code: disableCode }) });
                        const d = await r.json();
                        if (r.ok) {
                          setTwoFAEnabled(false);
                          if (onTotpChange) onTotpChange(false);
                          setShowDisable(false); setDisableCode("");
                        } else setTotpError(d.error || "Invalid code.");
                      } catch { setTotpError("Network error."); }
                      finally { setTotpLoading(false); }
                    }}
                  />
                </Modal>
              )}

              <div style={{ padding: "13px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: "13px", color: "#D4AF37" }}>Password</div>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "2px" }}>Last changed 60 days ago</div>
                  </div>
                  <ActionButton label={showPwChange ? "Cancel" : "Change"} onClick={() => setShowPwChange(p => !p)} />
                </div>
                {showPwChange && (
                  <div style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    {[{ label: "Current password", key: "current" }, { label: "New password", key: "next" }, { label: "Confirm new", key: "confirm" }].map(({ label, key }) => (
                      <input
                        key={key} type="password" placeholder={label}
                        value={pwForm[key]}
                        onChange={e => setPwForm(p => ({ ...p, [key]: e.target.value }))}
                        style={{
                          background: "var(--bg-main)", border: "1px solid var(--border-strong)", borderRadius: "8px",
                          padding: "9px 12px", fontSize: "12px", color: "#D4AF37",
                          fontFamily: "'JetBrains Mono', monospace", outline: "none", width: "100%", boxSizing: "border-box",
                        }}
                      />
                    ))}
                    {/* Live password requirements checklist */}
                    <div style={{ background: "var(--bg-main)", border: "1px solid #222018", borderRadius: "7px", padding: "8px 12px" }}>
                      {[
                        { test: pwForm.next.length >= 8,                                              label: "8+ characters" },
                        { test: /[A-Z]/.test(pwForm.next),                                            label: "Uppercase letter" },
                        { test: /[0-9]/.test(pwForm.next),                                            label: "Number (0–9)" },
                        { test: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwForm.next),           label: "Special character (!@#$…)" },
                      ].map(({ test, label }) => (
                        <div key={label} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "2px" }}>
                          <span style={{ fontSize: "11px", color: test ? "#6fcf6f" : "#444" }}>{test ? "✓" : "○"}</span>
                          <span style={{ fontSize: "11px", color: test ? "#9ae09a" : "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{label}</span>
                        </div>
                      ))}
                    </div>
                    {pwError && (
                      <div style={{ fontSize: "12px", color: "#e07070", fontFamily: "'JetBrains Mono', monospace", padding: "8px 10px", background: "#2d1a1a", borderRadius: "6px", border: "1px solid #5a2a2a" }}>{pwError}</div>
                    )}
                    {pwSaved
                      ? <div style={{ fontSize: "12px", color: "#6fcf6f", fontFamily: "'JetBrains Mono', monospace" }}>✅ Password updated</div>
                      : <ActionButton label={pwLoading ? "Updating…" : "Update Password"} variant="primary" onClick={handleSavePw} />}
                  </div>
                )}
              </div>

              <div style={{ padding: "6px 0" }}>
                <InfoRow label="LAST LOGIN" value="Today, 5:43 PM" />
              </div>
            </Section>
          </div>

          {/* Appearance */}
          <div style={{ background: "var(--bg-card)", borderRadius: "16px", padding: "24px", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>
              Appearance
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              {[
                { id: "dark",  label: "Dark",  icon: "◑", desc: "Forest green & gold" },
                { id: "light", label: "Light", icon: "☀", desc: "Clean white & gold"  },
              ].map(opt => {
                const isSelected = isDark ? opt.id === "dark" : opt.id === "light";
                return (
                  <button
                    key={opt.id}
                    onClick={() => applyTheme(opt.id)}
                    style={{
                      flex: 1,
                      padding: "14px 12px",
                      borderRadius: "12px",
                      background: isSelected ? "rgba(212,175,55,0.1)" : "var(--bg-surface)",
                      border: isSelected ? "1px solid rgba(212,175,55,0.5)" : "1px solid var(--border)",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "all 0.2s",
                    }}
                  >
                    <div style={{ fontSize: "20px", marginBottom: "6px" }}>{opt.icon}</div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: isSelected ? "#D4AF37" : "var(--text-primary)", fontFamily: "'Capsule Sans', sans-serif", marginBottom: "2px" }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
                      {opt.desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notifications */}
          <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px" }}>
            <Section title="Notifications">
              {notifError && (
                <div style={{ background: "#2d1a1a", border: "1px solid #5a2a2a", borderRadius: "8px", padding: "10px 14px", color: "#e07070", fontSize: "12px", marginBottom: "14px", fontFamily: "'JetBrains Mono', monospace" }}>
                  {notifError}
                </div>
              )}
              {[
                { label: "Email alerts",       sub: "Security & transactions", state: emailAlerts,  toggle: handleEmailAlertsToggle,  disabled: notifLoading },
                { label: "Push notifications", sub: "Mobile & desktop",        state: pushNotif,    toggle: handlePushNotifToggle,    disabled: notifLoading },
                { label: "Weekly report",      sub: "Financial summary email", state: weeklyReport, toggle: handleWeeklyReportToggle, disabled: notifLoading },
              ].map(({ label, sub, state, toggle, disabled }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid var(--border)", opacity: disabled ? 0.6 : 1 }}>
                  <div>
                    <div style={{ fontSize: "13px", color: "#D4AF37" }}>{label}</div>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "2px" }}>{sub}</div>
                  </div>
                  <Toggle on={state} onToggle={disabled ? undefined : toggle} />
                </div>
              ))}
            </Section>
          </div>
        </div>
      </div>

      {/* ── Manage Billing Modal ──────────────────────────────────── */}
      {modal === "billing" && (
        <Modal title="Billing & Subscription" onClose={() => setModal(null)}>
          <div style={{ marginBottom: "20px" }}>
            {[
              ["Plan",        "Premium — $9.99 / month"],
              ["Status",      "Active"],
              ["Last charge", "Mar 1, 2026 · $9.99"],
              ["Next charge", "Apr 1, 2026 · $9.99"],
              ["Card on file","Visa ending ·· 4242"],
            ].map(([label, value]) => (
              <div key={label} style={{
                display: "flex", justifyContent: "space-between",
                padding: "11px 0", borderBottom: "1px solid var(--border)",
              }}>
                <span style={{ fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>{label}</span>
                <span style={{ fontSize: "13px", color: "#D4AF37" }}>{value}</span>
              </div>
            ))}
          </div>
          <div style={{
            background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "10px",
            padding: "14px", marginBottom: "20px",
            fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.6,
          }}>
            To update your payment method or download invoices, a full billing portal is coming soon. Contact support@ledger.app for immediate billing assistance.
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <ActionButton label="Close" onClick={() => setModal(null)} />
          </div>
        </Modal>
      )}

      {/* ── Cancel Plan Modal ─────────────────────────────────────── */}
      {modal === "cancel" && (
        <Modal title={cancelStep === 1 ? "Cancel Subscription" : "Cancellation Confirmed"} onClose={() => setModal(null)}>
          {cancelStep === 1 ? (
            <>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: 1.7, marginBottom: "20px" }}>
                Are you sure you want to cancel your Premium plan? You'll lose access to:
              </div>
              <div style={{ marginBottom: "20px" }}>
                {["Unlimited bank connections via Plaid", "AI-powered budget insights", "Investment portfolio tracking", "Priority support"].map(item => (
                  <div key={item} style={{
                    padding: "8px 0", borderBottom: "1px solid var(--border)",
                    fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace",
                    display: "flex", gap: "8px",
                  }}>
                    <span style={{ color: "#e07070" }}>✗</span> {item}
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>
                  Reason (optional)
                </label>
                <select
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  style={{
                    width: "100%", padding: "10px 14px", background: "var(--bg-main)",
                    border: "1px solid var(--border-strong)", borderRadius: "8px",
                    color: cancelReason ? "#E8DDD0" : "#444", fontSize: "13px",
                    fontFamily: "inherit", outline: "none",
                  }}
                >
                  <option value="">Select a reason…</option>
                  <option value="too-expensive">Too expensive</option>
                  <option value="not-using">Not using it enough</option>
                  <option value="missing-feature">Missing a feature I need</option>
                  <option value="switching">Switching to another app</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <ActionButton label="Keep My Plan" variant="primary" onClick={() => setModal(null)} />
                <ActionButton label="Yes, Cancel" variant="danger" onClick={handleConfirmCancel} />
              </div>
            </>
          ) : (
            <>
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: "40px", marginBottom: "16px" }}>✓</div>
                <div style={{ fontSize: "15px", color: "var(--text-primary)", marginBottom: "8px" }}>Cancellation requested</div>
                <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.7 }}>
                  Your plan stays active until Apr 1, 2026.<br />
                  You won't be charged again. A confirmation email has been sent to revanth@ledger.app.
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
                <ActionButton label="Close" onClick={() => setModal(null)} />
              </div>
            </>
          )}
        </Modal>
      )}

      {/* ── Upgrade Modal ────────────────────────────────────────── */}
      {showUpgrade && (
        <UpgradeModal
          daysLeft={daysLeft}
          onConfirm={() => { onUpgrade && onUpgrade(); setShowUpgrade(false); }}
          onClose={() => setShowUpgrade(false)}
        />
      )}

      {/* ── Edit Preferences Modal ────────────────────────────────── */}
      {modal === "preferences" && (
        <Modal title="Edit Preferences" onClose={() => setModal(null)}>
          <SelectField
            label="Currency"
            value={prefsDraft.currency}
            onChange={v => setPrefsDraft(p => ({ ...p, currency: v }))}
            options={[
              { value: "USD", label: "USD — US Dollar" },
              { value: "EUR", label: "EUR — Euro" },
              { value: "GBP", label: "GBP — British Pound" },
              { value: "CAD", label: "CAD — Canadian Dollar" },
              { value: "AUD", label: "AUD — Australian Dollar" },
              { value: "JPY", label: "JPY — Japanese Yen" },
              { value: "INR", label: "INR — Indian Rupee" },
            ]}
          />
          <SelectField
            label="Timezone"
            value={prefsDraft.timezone}
            onChange={v => setPrefsDraft(p => ({ ...p, timezone: v }))}
            options={[
              { value: "America/New_York",    label: "Eastern Time (ET)" },
              { value: "America/Chicago",     label: "Central Time (CT)" },
              { value: "America/Denver",      label: "Mountain Time (MT)" },
              { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
              { value: "America/Anchorage",   label: "Alaska Time (AKT)" },
              { value: "Pacific/Honolulu",    label: "Hawaii Time (HT)" },
              { value: "Europe/London",       label: "GMT / London" },
              { value: "Europe/Paris",        label: "Central European Time" },
              { value: "Asia/Kolkata",        label: "India Standard Time" },
              { value: "Asia/Tokyo",          label: "Japan Standard Time" },
              { value: "Australia/Sydney",    label: "Australian Eastern Time" },
            ]}
          />
          <SelectField
            label="Date Format"
            value={prefsDraft.dateFormat}
            onChange={v => setPrefsDraft(p => ({ ...p, dateFormat: v }))}
            options={[
              { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US)" },
              { value: "DD/MM/YYYY", label: "DD/MM/YYYY (EU)" },
              { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO)" },
            ]}
          />
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "8px" }}>
            <ActionButton label="Cancel" onClick={() => setModal(null)} />
            {prefsSaved
              ? <div style={{ padding: "9px 18px", fontSize: "12px", color: "#6fcf6f", fontFamily: "'JetBrains Mono', monospace" }}>✅ Saved</div>
              : <ActionButton label="Save Preferences" variant="primary" onClick={handleSavePrefs} />}
          </div>
        </Modal>
      )}

    </div>
  );
}
